import React, { useState, useRef } from "react";
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { app } from "../../firebase";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { useToast } from "../../hooks/use-toast";
import { File, Upload, X } from "lucide-react";

interface FileUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFileUpload: (files: File[]) => void;
}

const FileUploadDialog = ({ open, onOpenChange, onFileUpload }: FileUploadDialogProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number[]>([]); // per-file progress
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileArray = Array.from(e.target.files);
      setFiles((prevFiles) => [...prevFiles, ...fileArray]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const fileArray = Array.from(e.dataTransfer.files);
      setFiles((prevFiles) => [...prevFiles, ...fileArray]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select files to upload",
        variant: "destructive",
      });
      return;
    }
    setUploading(true);
    setProgress(Array(files.length).fill(0));
    const storage = getStorage(app);
    let completed = 0;
    const uploadedFiles: File[] = [];

    await Promise.all(files.map((file, idx) => {
      return new Promise<void>((resolve, reject) => {
        const storageRef = ref(storage, `uploads/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);
        uploadTask.on('state_changed',
          (snapshot) => {
            const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setProgress(prev => {
              const updated = [...prev];
              updated[idx] = percent;
              return updated;
            });
          },
          (error) => {
            setProgress(prev => {
              const updated = [...prev];
              updated[idx] = -1; // error
              return updated;
            });
            reject(error);
          },
          () => {
            completed++;
            uploadedFiles.push(file);
            resolve();
          }
        );
      });
    }));

    onFileUpload(uploadedFiles);
    toast({
      title: "Upload complete",
      description: `Successfully uploaded ${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}`,
    });
    setFiles([]);
    setUploading(false);
    setProgress([]);
    onOpenChange(false);
  };

  const getFileIcon = (file: File) => {
    const fileType = file.type.split('/')[0];
    switch (fileType) {
      case 'image':
        return <div className="w-10 h-10 bg-green-100 rounded-md flex items-center justify-center"><File className="h-6 w-6 text-green-500" /></div>;
      case 'video':
        return <div className="w-10 h-10 bg-purple-100 rounded-md flex items-center justify-center"><File className="h-6 w-6 text-purple-500" /></div>;
      case 'audio':
        return <div className="w-10 h-10 bg-yellow-100 rounded-md flex items-center justify-center"><File className="h-6 w-6 text-yellow-500" /></div>;
      default:
        return <div className="w-10 h-10 bg-blue-100 rounded-md flex items-center justify-center"><File className="h-6 w-6 text-blue-500" /></div>;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md md:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Files</DialogTitle>
        </DialogHeader>
        
        <div 
          className="grid w-full items-center gap-4"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <div 
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-8 w-8 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              Drag and drop files here, or click to select files
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Supports all file types
            </p>
            <Input 
              ref={fileInputRef}
              type="file" 
              className="hidden" 
              onChange={handleFileChange} 
              multiple 
            />
          </div>
          
          {files.length > 0 && (
            <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
              {files.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 border rounded-md">
                  <div className="flex items-center space-x-3">
                    {getFileIcon(file)}
                    <div className="flex flex-col">
                      <span className="text-sm font-medium truncate max-w-[200px]">{file.name}</span>
                      <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                      {uploading && (
                        <div className="w-full mt-1">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-2 rounded-full ${progress[index] === 100 ? 'bg-green-500' : progress[index] === -1 ? 'bg-red-500' : 'bg-blue-500'}`}
                              style={{ width: `${Math.max(0, progress[index] || 0)}%`, transition: 'width 0.3s' }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{progress[index] === -1 ? 'Error' : `${progress[index] || 0}%`}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={() => removeFile(index)}
                    disabled={uploading}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row sm:justify-between sm:space-x-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button 
            type="button"
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            className="gap-2"
          >
            {uploading ? "Uploading..." : "Upload Files"}
            {!uploading && <Upload className="h-4 w-4" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FileUploadDialog;
